// Admin: what Coach MARK costs (mark.js /mark/costs). The last six months in total, this month's
// pace, and every member with their cost against the monthly cap, last month, their 3-month average
// and whether they're consistently among the heaviest users. Screenshot imports are shown apart:
// they aren't MARK and don't count toward the cap.
import { useEffect, useMemo, useState } from 'react';
import { api } from './api';

const usd = (n) => (!n ? '$0' : `$${n.toFixed(n < 1 ? 3 : 2)}`);
const monthName = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
const PLAN_NAMES = { pro: 'Pro', trial: 'Trial', free: 'Free', admin: 'Admin', deleted: '—' };
const SORTS = [['now', 'This month'], ['avg', '3-month average'], ['last', 'Last month']];
const OPEN_KEY = 'mm-admin-markcosts-open';

export default function MarkCosts() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sort, setSort] = useState('now');
  const [open, setOpen] = useState(() => {
    try {
      return localStorage.getItem(OPEN_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  useEffect(() => { api('/mark/costs').then(setData).catch((err) => setError(err.message)); }, []);
  const view = useMemo(() => {
    if (!data) return null;
    const [now, last] = data.months;
    const recent = data.months.slice(0, 3);
    const mark = (r) => r.kind === 'chat' || r.kind === 'debrief' || r.kind === 'tutor';
    const sum = (list) => list.reduce((s, r) => s + r.cost, 0);
    const totals = data.months.map((m) => ({ month: m, mark: sum(data.rows.filter((r) => r.month === m && mark(r))), imports: sum(data.rows.filter((r) => r.month === m && r.kind === 'import')) }));
    // Each member: this month by kind, last month, 3-month average (months they used MARK), and how
    // many of the last 3 months they were in the top 3.
    const top3 = Object.fromEntries(recent.map((m) => {
      const per = {};
      for (const r of data.rows.filter((x) => x.month === m && mark(x))) per[r.userId] = (per[r.userId] || 0) + r.cost;
      return [m, Object.entries(per).sort((a, b) => b[1] - a[1]).slice(0, 3).filter(([, c]) => c > 0.5).map(([id]) => id)];
    }));
    const members = data.members.map((mem) => {
      const mine = data.rows.filter((r) => r.userId === mem.userId);
      const of = (m, kind) => sum(mine.filter((r) => r.month === m && (kind ? r.kind === kind : mark(r))));
      const calls = (m, kind) => mine.filter((r) => r.month === m && r.kind === kind).reduce((s, r) => s + r.calls, 0);
      const used = recent.map((m) => of(m)).filter((c) => c > 0);
      const avg = used.length ? used.reduce((s, c) => s + c, 0) / used.length : 0;
      const inTop = recent.filter((m) => top3[m].includes(mem.userId)).length;
      return {
        ...mem, now: of(now), last: of(last), avg, chat: of(now, 'chat'), debrief: of(now, 'debrief'), imports: of(now, 'import'),
        chats: calls(now, 'chat'), debriefs: calls(now, 'debrief'), inTop,
        steady: used.length >= 2 && (avg >= data.cap * 0.5 || inTop >= 2),
      };
    }).filter((m) => m.now || m.last || m.avg || m.imports);
    members.sort((a, b) => (b[sort] || 0) - (a[sort] || 0));
    const day = new Date().getUTCDate();
    const daysInMonth = new Date(Date.UTC(new Date().getUTCFullYear(), new Date().getUTCMonth() + 1, 0)).getUTCDate();
    const active = members.filter((m) => m.now > 0);
    return {
      now, totals, members, active,
      // A month's pace only means something after the first few days.
      pace: day >= 5 ? (totals[0].mark / day) * daysInMonth : null,
      nearCap: active.filter((m) => m.now >= data.cap * 0.7).length,
      steady: members.filter((m) => m.steady).length,
    };
  }, [data, sort]);

  function toggle() {
    setOpen((o) => {
      try {
        localStorage.setItem(OPEN_KEY, o ? 'closed' : 'open');
      } catch {
        // Not remembered in private mode.
      }
      return !o;
    });
  }

  if (error) return <section className="admin-panel"><h2>Coach MARK costs</h2><p className="error">{error}</p></section>;
  if (!data || !view) return null;
  const maxMonth = Math.max(0.01, ...view.totals.map((x) => x.mark + x.imports));
  return (
    <section className="admin-panel admin-preview-panel mkc">
      <div className="admin-panel-header">
        <div>
          <h2>
            <button type="button" className="panel-toggle" aria-expanded={open} onClick={toggle}>
              <span className={`panel-toggle-chevron${open ? ' open' : ''}`} aria-hidden="true">&#9656;</span>
              Coach MARK costs
              <span className="panel-toggle-count">{usd(view.totals[0].mark)} this month &bull; {view.active.length} using MARK{view.nearCap ? ` • ${view.nearCap} near the cap` : ''}</span>
            </button>
            {' '}<span className="admin-preview-badge">Admin only</span>
          </h2>
          {open && (
            <p className="admin-panel-sub">
              {data.ready ? `On, using ${data.model}` : 'Off: ANTHROPIC_API_KEY is not set on Render'} &bull; {data.public ? 'open to members' : 'admins only (Admin → Features opens it)'} &bull; Pro {data.limit} credits a month, trial {data.trialLimit} &bull; hard cap {usd(data.cap)} per member a month
            </p>
          )}
        </div>
      </div>
      {open && (
        <>
          <section className="summary admin-summary">
            <div className="stat"><span className="stat-label">This month</span><span className="stat-value">{usd(view.totals[0].mark)}</span><span className="stat-note">{view.pace === null ? 'pace shows from the 5th' : `on pace for ${usd(view.pace)}`}</span></div>
            <div className="stat"><span className="stat-label">Last month</span><span className="stat-value">{usd(view.totals[1].mark)}</span></div>
            <div className="stat"><span className="stat-label">Members using MARK</span><span className="stat-value">{view.active.length}</span>
              {view.active.length > 0 && <span className="stat-note">{usd(view.totals[0].mark / view.active.length)} each on average</span>}</div>
            <div className="stat"><span className="stat-label">Near the {usd(data.cap)} cap</span><span className={`stat-value${view.nearCap ? ' loss' : ''}`}>{view.nearCap}</span><span className="stat-note">70% or more of it this month</span></div>
            <div className="stat"><span className="stat-label">Consistently high</span><span className={`stat-value${view.steady ? ' loss' : ''}`}>{view.steady}</span><span className="stat-note">heavy two months or more</span></div>
          </section>

          <div className="mkc-months" aria-label="MARK cost by month">
            {[...view.totals].reverse().map((x) => (
              <div key={x.month} className="mkc-month" title={`${monthName(x.month)}: MARK ${usd(x.mark)}, screenshot imports ${usd(x.imports)}`}>
                <span className="mkc-bar"><b style={{ height: `${(x.mark / maxMonth) * 100}%` }} /><i style={{ height: `${(x.imports / maxMonth) * 100}%` }} /></span>
                <strong>{usd(x.mark)}</strong>
                <small>{monthName(x.month)}</small>
              </div>
            ))}
          </div>

          {view.members.length > 0 ? (
            <>
              <div className="mkc-tools">
                <h3>Members, heaviest first</h3>
                <div className="inbox-filter" role="group" aria-label="Sort members">
                  {SORTS.map(([id, label]) => <button key={id} type="button" className={`secondary small${sort === id ? ' active' : ''}`} aria-pressed={sort === id} onClick={() => setSort(id)}>{label}</button>)}
                </div>
              </div>
              <div className="table-wrap">
                <table className="biz-table mkc-table">
                  <thead>
                    <tr><th>Member</th><th>Plan</th><th>This month vs cap</th><th className="num">Chats</th><th className="num">Debriefs</th><th className="num">Credits</th><th className="num">Last month</th><th className="num">3-month avg</th><th className="num hide-sm">Imports</th></tr>
                  </thead>
                  <tbody>
                    {view.members.map((m) => {
                      const pct = Math.min(100, (m.now / data.cap) * 100);
                      return (
                        <tr key={m.userId}>
                          <th scope="row">
                            <span className="mkc-who">{m.email}</span>
                            {m.steady && <span className="mkc-flag" title="Heavy usage two months or more (3-month average at half the cap or more, or top 3 in two of the last three months)">Consistently high</span>}
                          </th>
                          <td><span className={`mkc-plan ${m.plan}`}>{PLAN_NAMES[m.plan] || m.plan}</span></td>
                          <td>
                            <span className="mkc-cap"><span className="mkc-track"><b className={pct >= 90 ? 'hot' : pct >= 70 ? 'warm' : ''} style={{ width: `${pct}%` }} /></span>
                              <strong>{usd(m.now)}</strong><small>{Math.round(pct)}%</small></span>
                            <small className="mkc-split">chat {usd(m.chat)} · debriefs {usd(m.debrief)}</small>
                          </td>
                          <td className="num">{m.chats}</td>
                          <td className="num">{m.debriefs}</td>
                          <td className="num">{m.plan === 'admin' ? '—' : `${m.credits} / ${m.creditLimit}`}</td>
                          <td className="num">{usd(m.last)}</td>
                          <td className="num">{usd(m.avg)}</td>
                          <td className="num hide-sm">{m.imports ? usd(m.imports) : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : <p className="empty">Nobody has used MARK yet.</p>}
          <p className="mk-costs-note">Costs are worked out from the tokens each call used, at Opus 5.5 prices ($4 in, $20 out, $0.20 cached per million). Your Anthropic Console shows the exact bill. Screenshot imports (light bar, last column) aren&apos;t MARK and don&apos;t count toward the cap. &ldquo;Consistently high&rdquo; means a 3-month average at half the cap or more, or top 3 in two of the last three months.</p>
        </>
      )}
    </section>
  );
}
